import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { useSupabaseQuery } from '../../hooks/useSupabaseQuery';
import { PageHeader } from '../../components/ui/PageHeader';
import { EmptyState } from '../../components/ui/EmptyState';
import type { ComisionConProfesional } from '../../types/domain';

const COP = new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });

function periodoActual(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** Comisión con el/los medio(s) de pago de la venta que la generó, a
 * quién se le prestó el servicio (clienta), cuál fue (nombre del
 * servicio) y la fecha de esa venta -- pedido explícito del usuario,
 * mismo hilo que el medio de pago: "que aparezca a quién se prestó el
 * servicio o la venta y cuál fue", extendido después con "poner la
 * fecha de la venta". */
interface ComisionConMedioPago extends ComisionConProfesional {
  medio_pago: string;
  clienta_nombre: string;
  servicio_nombre: string;
  venta_fecha: string | null;
}

type FiltroEstadoPago = 'todas' | 'pendientes' | 'pagadas';

/**
 * Comisiones (DAF v2, sección 22).
 *
 * Dos cosas independientes conviven en esta pantalla, a propósito
 * desacopladas:
 *
 * 1. "Liquidar período" (arriba): calcula comisiones NUEVAS para un
 *    período puntual, vía el RPC `liquidar_comisiones`
 *    (supabase/schema_comisiones_pagos.sql) — es seguro presionarlo
 *    varias veces para el mismo período (no duplica lo que ya liquidó).
 *
 * 2. La tabla de abajo (pedido explícito del usuario: "quiero que las
 *    comisiones estén anclado con el pago a empleados... que se puedan
 *    seleccionar el empleado y las comisiones a pagar... que me diga
 *    cuáles están pendientes por pagar y cuáles están pagas, me dé el
 *    total") -- lista TODAS las comisiones activas, de cualquier
 *    período, no solo el que esté elegido arriba. Si se filtrara por el
 *    período de "Liquidar período", una comisión pendiente de pago de
 *    hace dos meses quedaría invisible en cuanto alguien cambiara el
 *    selector de arriba para liquidar el mes actual.
 *
 * El pago en sí sigue sin reinventar nada: tanto el botón "Pagar" por
 * fila como el pago en lote llaman, en el servidor, al mismo camino que
 * ya existía (`pagar_empleado()`) -- ver
 * supabase/schema_comisiones_pago_multiple.sql. La única regla dura de
 * la selección múltiple es que un pago a empleado pertenece a un solo
 * profesional, así que solo se puede tener seleccionadas comisiones de
 * una misma profesional a la vez -- las casillas de las demás se
 * deshabilitan mientras haya una selección activa.
 */
export function Comisiones() {
  const { perfil } = useAuth();

  // --- Sección 1: liquidar período (genera comisiones nuevas) ---
  const [periodoLiquidar, setPeriodoLiquidar] = useState(periodoActual());
  const [liquidando, setLiquidando] = useState(false);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // --- Sección 2: tabla de comisiones, pago individual y en lote ---
  const [filtroProfesional, setFiltroProfesional] = useState<string>('todos');
  const [filtroEstadoPago, setFiltroEstadoPago] = useState<FiltroEstadoPago>('todas');
  const [seleccionadas, setSeleccionadas] = useState<Set<string>>(new Set());
  const [profesionalSeleccion, setProfesionalSeleccion] = useState<string | null>(null);
  const [pagando, setPagando] = useState<string | null>(null);
  const [errorPago, setErrorPago] = useState<string | null>(null);
  const [pagandoLote, setPagandoLote] = useState(false);

  const { data, loading, refetch } = useSupabaseQuery<ComisionConMedioPago[]>(async () => {
    // A propósito SIN filtro de período: esta tabla debe mostrar toda
    // comisión activa pendiente o pagada, sin importar de qué mes sea.
    const { data, error } = await supabase
      .from('comisiones')
      .select('*, profesionales ( nombre )')
      .eq('estado', 'activo')
      .order('periodo', { ascending: false })
      .order('created_at', { ascending: false });

    if (error) return { data: null, error };

    const items = (data as unknown as Array<Record<string, unknown>>).map((row) => ({
      ...(row as unknown as ComisionConProfesional),
      profesional_nombre: (row.profesionales as { nombre: string } | null)?.nombre ?? '—',
    }));

    // Cada comisión viene de una línea de venta (`venta_detalle_id`); de
    // ahí se sube a la venta y se trae con qué se pagó, a quién se le
    // prestó el servicio, cuál fue y en qué fecha -- pedido del usuario,
    // mismo hilo que el medio de pago ("que aparezca a quién se prestó el
    // servicio o la venta y cuál fue") extendido después con "poner la
    // fecha de la venta". Una venta puede tener varios pagos si se
    // dividió entre medios (efectivo + transferencia, por ejemplo --
    // función ya existente en Ventas), así que se listan todos los
    // medios encontrados en vez de asumir uno solo. `venta_detalle` ya
    // trae embebido el nombre del servicio (`servicios ( nombre )`,
    // mismo patrón que usan Cartera.tsx/Reportes.tsx); la clienta y la
    // fecha se resuelven en una consulta aparte sobre `ventas` porque
    // `comisiones` no tiene una relación directa a `ventas`/`clientes`
    // (pasa por `venta_detalle` → `ventas`).
    const idsDetalle = items.map((c) => c.venta_detalle_id).filter((id): id is string => Boolean(id));

    let medioPagoPorComision = new Map<string, string>();
    let servicioPorComision = new Map<string, string>();
    let clientaPorComision = new Map<string, string>();
    let fechaPorComision = new Map<string, string | null>();

    if (idsDetalle.length > 0) {
      const { data: detalles, error: errDetalles } = await supabase
        .from('venta_detalle')
        .select('id, venta_id, servicios ( nombre )')
        .in('id', idsDetalle);
      if (errDetalles) return { data: null, error: errDetalles };

      const ventaPorDetalle = new Map<string, string>((detalles ?? []).map((d) => [d.id, d.venta_id as string]));
      const servicioPorDetalle = new Map<string, string>(
        (detalles as unknown as Array<{ id: string; servicios: { nombre: string } | null }> | null ?? []).map((d) => [
          d.id,
          d.servicios?.nombre ?? '—',
        ]),
      );
      const idsVenta = [...new Set((detalles ?? []).map((d) => d.venta_id as string))];

      const [{ data: pagos, error: errPagos }, { data: ventas, error: errVentas }] = await Promise.all([
        supabase.from('pagos').select('venta_id, formas_pago ( nombre )').in('venta_id', idsVenta),
        supabase.from('ventas').select('id, fecha, clientes ( nombre, apellido )').in('id', idsVenta),
      ]);
      if (errPagos) return { data: null, error: errPagos };
      if (errVentas) return { data: null, error: errVentas };

      const mediosPorVenta = new Map<string, string[]>();
      for (const p of (pagos ?? []) as unknown as Array<{ venta_id: string; formas_pago: { nombre: string } | null }>) {
        const nombre = p.formas_pago?.nombre ?? '—';
        const lista = mediosPorVenta.get(p.venta_id) ?? [];
        if (!lista.includes(nombre)) lista.push(nombre);
        mediosPorVenta.set(p.venta_id, lista);
      }

      const clientaPorVenta = new Map<string, string>(
        (ventas as unknown as Array<{ id: string; fecha: string | null; clientes: { nombre: string; apellido: string | null } | null }> | null ?? []).map(
          (v) => [v.id, v.clientes ? [v.clientes.nombre, v.clientes.apellido].filter(Boolean).join(' ') : '—'],
        ),
      );

      const fechaVentaPorVenta = new Map<string, string | null>(
        (ventas as unknown as Array<{ id: string; fecha: string | null }> | null ?? []).map((v) => [v.id, v.fecha]),
      );

      medioPagoPorComision = new Map<string, string>(
        items.map((c) => {
          const ventaId = c.venta_detalle_id ? ventaPorDetalle.get(c.venta_detalle_id) : undefined;
          const medios = ventaId ? mediosPorVenta.get(ventaId) : undefined;
          return [c.id, medios && medios.length > 0 ? medios.join(' + ') : '—'];
        }),
      );

      servicioPorComision = new Map<string, string>(
        items.map((c) => [c.id, c.venta_detalle_id ? servicioPorDetalle.get(c.venta_detalle_id) ?? '—' : '—']),
      );

      clientaPorComision = new Map<string, string>(
        items.map((c) => {
          const ventaId = c.venta_detalle_id ? ventaPorDetalle.get(c.venta_detalle_id) : undefined;
          return [c.id, ventaId ? clientaPorVenta.get(ventaId) ?? '—' : '—'];
        }),
      );

      fechaPorComision = new Map<string, string | null>(
        items.map((c) => {
          const ventaId = c.venta_detalle_id ? ventaPorDetalle.get(c.venta_detalle_id) : undefined;
          return [c.id, ventaId ? fechaVentaPorVenta.get(ventaId) ?? null : null];
        }),
      );
    }

    const itemsConMedio = items.map((c) => ({
      ...c,
      medio_pago: medioPagoPorComision.get(c.id) ?? '—',
      servicio_nombre: servicioPorComision.get(c.id) ?? '—',
      clienta_nombre: clientaPorComision.get(c.id) ?? '—',
      venta_fecha: fechaPorComision.get(c.id) ?? null,
    }));

    return { data: itemsConMedio, error: null };
  });

  /**
   * Pago de una sola comisión (botón "Pagar" de la fila). Sigue llamando
   * `pagar_comision()`, que desde `schema_comisiones_pago_multiple.sql`
   * es una envoltura sobre `pagar_comisiones([id])` -- mismo
   * comportamiento de siempre, ninguna lógica duplicada aquí.
   */
  async function pagarComision(id: string) {
    setPagando(id);
    setErrorPago(null);

    const { error: err } = await supabase.rpc('pagar_comision', { p_comision_id: id });

    setPagando(null);

    if (err) {
      setErrorPago(err.message);
      return;
    }

    refetch();
  }

  function alternarSeleccion(c: ComisionConMedioPago) {
    if (c.pago_empleado_id) return; // ya pagada, no se puede seleccionar

    setSeleccionadas((prev) => {
      const next = new Set(prev);
      if (next.has(c.id)) {
        next.delete(c.id);
        if (next.size === 0) setProfesionalSeleccion(null);
      } else {
        // Una sola profesional a la vez: si ya hay una selección activa
        // de otra profesional, se ignora (la casilla debería estar
        // deshabilitada en la UI, esto es una segunda barrera).
        if (profesionalSeleccion && profesionalSeleccion !== c.profesional_id) return prev;
        setProfesionalSeleccion(c.profesional_id);
        next.add(c.id);
      }
      return next;
    });
  }

  function seleccionarPendientesDe(profesionalId: string) {
    const pendientes = (data ?? []).filter((c) => c.profesional_id === profesionalId && !c.pago_empleado_id);
    setProfesionalSeleccion(profesionalId);
    setSeleccionadas(new Set(pendientes.map((c) => c.id)));
  }

  function limpiarSeleccion() {
    setSeleccionadas(new Set());
    setProfesionalSeleccion(null);
  }

  async function pagarSeleccionadas() {
    if (seleccionadas.size === 0) return;
    setPagandoLote(true);
    setErrorPago(null);

    const { error: err } = await supabase.rpc('pagar_comisiones', { p_comision_ids: Array.from(seleccionadas) });

    setPagandoLote(false);

    if (err) {
      setErrorPago(err.message);
      return; // se deja la selección para que se pueda reintentar o ajustar
    }

    limpiarSeleccion();
    refetch();
  }

  async function liquidar() {
    if (!perfil) return;
    setLiquidando(true);
    setMensaje(null);
    setError(null);

    const { data, error: err } = await supabase.rpc('liquidar_comisiones', {
      p_salon_id: perfil.salon_id,
      p_periodo: periodoLiquidar,
    });

    setLiquidando(false);

    if (err) {
      setError(err.message);
      return;
    }

    setMensaje(data === 0 ? 'No hay comisiones nuevas por generar para este período.' : `Se generaron ${data} comisión(es) nueva(s).`);
    refetch();
  }

  // --- Derivados: resumen por profesional (Pendiente/Pagado/Total) y filtros ---

  const profesionales = useMemo(() => {
    const mapa = new Map<string, string>();
    for (const c of data ?? []) mapa.set(c.profesional_id, c.profesional_nombre);
    return Array.from(mapa.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [data]);

  const resumenPorProfesional = useMemo(() => {
    const mapa = new Map<string, { nombre: string; pendiente: number; pagado: number }>();
    for (const c of data ?? []) {
      if (filtroProfesional !== 'todos' && c.profesional_id !== filtroProfesional) continue;
      const actual = mapa.get(c.profesional_id) ?? { nombre: c.profesional_nombre, pendiente: 0, pagado: 0 };
      if (c.pago_empleado_id) actual.pagado += c.monto_comision;
      else actual.pendiente += c.monto_comision;
      mapa.set(c.profesional_id, actual);
    }
    return Array.from(mapa.entries()).sort((a, b) => a[1].nombre.localeCompare(b[1].nombre));
  }, [data, filtroProfesional]);

  const filas = useMemo(() => {
    return (data ?? []).filter((c) => {
      if (filtroProfesional !== 'todos' && c.profesional_id !== filtroProfesional) return false;
      if (filtroEstadoPago === 'pendientes' && c.pago_empleado_id) return false;
      if (filtroEstadoPago === 'pagadas' && !c.pago_empleado_id) return false;
      return true;
    });
  }, [data, filtroProfesional, filtroEstadoPago]);

  const totalGeneral = { pendiente: 0, pagado: 0 };
  for (const [, r] of resumenPorProfesional) {
    totalGeneral.pendiente += r.pendiente;
    totalGeneral.pagado += r.pagado;
  }

  const totalSeleccionado = (data ?? [])
    .filter((c) => seleccionadas.has(c.id))
    .reduce((sum, c) => sum + c.monto_comision, 0);

  return (
    <div>
      <PageHeader
        title="Comisiones"
        description="Cálculo y pago de comisiones — DAF v2, sección 22"
        actions={
          <Link to="/admin/comisiones/esquemas" className="btn-secondary">
            Esquemas de remuneración
          </Link>
        }
      />

      <div className="card mb-4 flex flex-wrap items-end gap-4">
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-onyx/80">Liquidar período</span>
          <input type="month" className="input-field" value={periodoLiquidar} onChange={(e) => setPeriodoLiquidar(e.target.value)} />
        </label>
        <button type="button" disabled={liquidando} className="btn-primary" onClick={liquidar}>
          {liquidando ? 'Liquidando…' : 'Liquidar período'}
        </button>
        {mensaje && <p className="text-sm text-green-700">{mensaje}</p>}
        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>

      <div className="card mb-4 flex flex-wrap items-end gap-4">
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-onyx/80">Profesional</span>
          <select
            className="input-field"
            value={filtroProfesional}
            onChange={(e) => setFiltroProfesional(e.target.value)}
          >
            <option value="todos">Todas</option>
            {profesionales.map(([id, nombre]) => (
              <option key={id} value={id}>
                {nombre}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-onyx/80">Estado de pago</span>
          <select
            className="input-field"
            value={filtroEstadoPago}
            onChange={(e) => setFiltroEstadoPago(e.target.value as FiltroEstadoPago)}
          >
            <option value="todas">Todas</option>
            <option value="pendientes">Pendientes por pagar</option>
            <option value="pagadas">Pagadas</option>
          </select>
        </label>
      </div>

      {resumenPorProfesional.length > 0 && (
        <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {resumenPorProfesional.map(([id, r]) => (
            <div key={id} className="card">
              <p className="text-xs text-onyx/50">{r.nombre}</p>
              <div className="mt-1 flex items-baseline justify-between">
                <span className="text-xs text-amber-700">Pendiente</span>
                <span className="font-display text-onyx">{COP.format(r.pendiente)}</span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-xs text-green-700">Pagado</span>
                <span className="font-display text-onyx">{COP.format(r.pagado)}</span>
              </div>
              <div className="mt-1 flex items-baseline justify-between border-t border-onyx/10 pt-1">
                <span className="text-xs font-medium text-onyx/60">Total</span>
                <span className="font-display text-gold-deep">{COP.format(r.pendiente + r.pagado)}</span>
              </div>
              {r.pendiente > 0 && (
                <button
                  type="button"
                  className="btn-secondary mt-2 w-full px-3 py-1 text-xs"
                  onClick={() => seleccionarPendientesDe(id)}
                >
                  Seleccionar sus pendientes
                </button>
              )}
            </div>
          ))}
          <div className="card bg-onyx/5">
            <p className="text-xs text-onyx/50">Total general</p>
            <div className="mt-1 flex items-baseline justify-between">
              <span className="text-xs text-amber-700">Pendiente</span>
              <span className="font-display text-onyx">{COP.format(totalGeneral.pendiente)}</span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-xs text-green-700">Pagado</span>
              <span className="font-display text-onyx">{COP.format(totalGeneral.pagado)}</span>
            </div>
            <div className="mt-1 flex items-baseline justify-between border-t border-onyx/10 pt-1">
              <span className="text-xs font-medium text-onyx/60">Total</span>
              <span className="font-display text-gold-deep">{COP.format(totalGeneral.pendiente + totalGeneral.pagado)}</span>
            </div>
          </div>
        </div>
      )}

      {seleccionadas.size > 0 && (
        <div className="card mb-4 flex flex-wrap items-center justify-between gap-3 border-l-4 border-gold-deep">
          <p className="text-sm text-onyx">
            <span className="font-medium">{seleccionadas.size}</span> comisión(es) seleccionada(s) ·{' '}
            <span className="font-display text-gold-deep">{COP.format(totalSeleccionado)}</span>
          </p>
          <div className="flex gap-2">
            <button type="button" className="btn-secondary px-3 py-1 text-xs" onClick={limpiarSeleccion}>
              Cancelar selección
            </button>
            <button type="button" disabled={pagandoLote} className="btn-primary px-3 py-1 text-xs" onClick={pagarSeleccionadas}>
              {pagandoLote ? 'Pagando…' : 'Pagar seleccionadas'}
            </button>
          </div>
        </div>
      )}

      {errorPago && <p className="mb-4 text-sm text-red-600">{errorPago}</p>}

      {loading && <p className="text-sm text-onyx/50">Cargando…</p>}

      {data && data.length === 0 && !loading && (
        <EmptyState title="Sin comisiones liquidadas" description="Usa «Liquidar período» de arriba para generarlas." />
      )}

      {filas.length > 0 && (
        <div className="card overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-onyx/10 text-left text-onyx/50">
                <th className="px-4 py-3 font-medium"></th>
                <th className="px-4 py-3 font-medium">Profesional</th>
                <th className="px-4 py-3 font-medium">Clienta</th>
                <th className="px-4 py-3 font-medium">Servicio</th>
                <th className="px-4 py-3 font-medium">Fecha de venta</th>
                <th className="px-4 py-3 font-medium">Período</th>
                <th className="px-4 py-3 font-medium">Base</th>
                <th className="px-4 py-3 font-medium">%</th>
                <th className="px-4 py-3 font-medium">Comisión</th>
                <th className="px-4 py-3 font-medium">Medio de pago</th>
                <th className="px-4 py-3 font-medium">Pago</th>
              </tr>
            </thead>
            <tbody>
              {filas.map((c) => {
                const pagada = Boolean(c.pago_empleado_id);
                const deshabilitadaPorOtraProfesional = !pagada && profesionalSeleccion !== null && profesionalSeleccion !== c.profesional_id;
                return (
                  <tr key={c.id} className="border-b border-onyx/5 last:border-0">
                    <td className="px-4 py-3">
                      {!pagada && (
                        <input
                          type="checkbox"
                          checked={seleccionadas.has(c.id)}
                          disabled={deshabilitadaPorOtraProfesional}
                          onChange={() => alternarSeleccion(c)}
                        />
                      )}
                    </td>
                    <td className="px-4 py-3 text-onyx">{c.profesional_nombre}</td>
                    <td className="px-4 py-3 text-onyx/70">{c.clienta_nombre}</td>
                    <td className="px-4 py-3 text-onyx/70">{c.servicio_nombre}</td>
                    <td className="px-4 py-3 text-onyx/70">
                      {c.venta_fecha
                        ? new Date(c.venta_fecha).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' })
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-onyx/70">{c.periodo}</td>
                    <td className="px-4 py-3 text-onyx/70">{COP.format(c.monto_base)}</td>
                    <td className="px-4 py-3 text-onyx/70">{c.porcentaje}%</td>
                    <td className="px-4 py-3 font-medium text-onyx">{COP.format(c.monto_comision)}</td>
                    <td className="px-4 py-3 text-onyx/70">{c.medio_pago}</td>
                    <td className="px-4 py-3">
                      {pagada ? (
                        <span className="badge bg-green-100 text-green-800">Pagada</span>
                      ) : (
                        <button
                          type="button"
                          disabled={pagando === c.id}
                          className="btn-secondary px-3 py-1 text-xs"
                          onClick={() => pagarComision(c.id)}
                        >
                          {pagando === c.id ? 'Pagando…' : 'Pagar'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {data && data.length > 0 && filas.length === 0 && (
        <EmptyState title="Sin comisiones para este filtro" description="Prueba con otro profesional o estado de pago." />
      )}
    </div>
  );
}
